import { nextTick, ref, shallowRef, type Component, type Ref, type ShallowRef } from 'vue';

// Define options specific to the Drawer shell itself
export interface DrawerOptions {
  title?: string;
  closable?: boolean;
  hideCloseButton?: boolean; // hides X button but still allows closing via drag gesture or clicking outside
  full?: boolean;
  fullMobile?: boolean; // true = 100dvh × 100vw fullscreen on mobile (no rounding, covers safe areas)
  noPadding?: boolean;
  bgColor?: string;
  onBack?: () => void; // when set, shows a leading back-arrow button in the header that calls this
}

// Desktop breakpoint (matches Tailwind's lg breakpoint)
const DESKTOP_BREAKPOINT = 1024;

// Define the state interface
interface DrawerState {
  isOpen: Ref<boolean>;
  activeComponent: ShallowRef<Component | null>;
  componentProps: Ref<Record<string, any>>;
  drawerOptions: Ref<DrawerOptions>;
  isDesktop: Ref<boolean>;
  openDrawer: (component: Component, props?: Record<string, any>, options?: DrawerOptions) => void;
  closeDrawer: () => void;
  // Bumped on every open so the rendered component remounts fresh (no stale setup state).
  drawerKey: Ref<number>;
  // Multi-level drawer state
  secondaryOpen: Ref<boolean>;
  secondaryComponent: ShallowRef<Component | null>;
  secondaryProps: Ref<Record<string, any>>;
  secondaryOptions: Ref<DrawerOptions>;
  secondaryKey: Ref<number>;
}

// Primary drawer state (global singleton)
const isOpen = ref(false);
const activeComponent = shallowRef<Component | null>(null);
const componentProps = ref<Record<string, any>>({});
const drawerOptions = ref<DrawerOptions>({ closable: true });
const drawerKey = ref(0);

// Secondary drawer state (global singleton)
const secondaryOpen = ref(false);
const secondaryComponent = shallowRef<Component | null>(null);
const secondaryProps = ref<Record<string, any>>({});
const secondaryOptions = ref<DrawerOptions>({ closable: true });
const secondaryKey = ref(0);

const isDesktop = ref(typeof window !== 'undefined' ? window.innerWidth >= DESKTOP_BREAKPOINT : false);

// Update desktop state on resize
const updateDesktopState = () => {
  isDesktop.value = window.innerWidth >= DESKTOP_BREAKPOINT;
};

// Close drawer on ESC key (desktop only)
const handleEscKey = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isDesktop.value && (isOpen.value || secondaryOpen.value)) {
    closeDrawerInternal();
  }
};

// Internal close logic (used by both closeDrawer and ESC handler)
const closeDrawerInternal = () => {
  if (secondaryOpen.value) {
    secondaryOpen.value = false;
    setTimeout(() => {
      secondaryComponent.value = null;
      secondaryProps.value = {};
      secondaryOptions.value = { closable: true };
    }, 300);
  } else if (isOpen.value) {
    isOpen.value = false;
    document.body.style.overflow = '';
    setTimeout(() => {
      activeComponent.value = null;
      componentProps.value = {};
      drawerOptions.value = { closable: true };
    }, 300);
  }
};

// Initialize global listeners (only once)
let resizeListenerInitialized = false;
const initResizeListener = () => {
  if (resizeListenerInitialized || typeof window === 'undefined') return;
  window.addEventListener('resize', updateDesktopState);
  window.addEventListener('keydown', handleEscKey);
  resizeListenerInitialized = true;
};

export function useDrawer(): DrawerState {
  // Initialize resize listener when composable is first used
  initResizeListener();

  const openDrawer = (
    component: Component,
    props: Record<string, any> = {},
    options?: DrawerOptions
  ) => {
    const mergedOptions: DrawerOptions = { closable: true, ...options };

    if (isOpen.value) {
      // Primary already open -> open as secondary (max 2)
      if (secondaryOpen.value) return;
      secondaryComponent.value = component;
      secondaryProps.value = props;
      secondaryOptions.value = mergedOptions;
      secondaryKey.value++;
      nextTick(() => {
        secondaryOpen.value = true;
      });
    } else {
      // Open as primary
      activeComponent.value = component;
      componentProps.value = props;
      drawerOptions.value = mergedOptions;
      drawerKey.value++;
      nextTick(() => {
        isOpen.value = true;
        document.body.style.overflow = 'hidden';
      });
    }
  };

  const closeDrawer = () => {
    closeDrawerInternal();
  };

  return {
    isOpen,
    activeComponent,
    componentProps,
    drawerOptions,
    isDesktop,
    openDrawer,
    closeDrawer,
    drawerKey,
    // Multi-level
    secondaryOpen,
    secondaryComponent,
    secondaryProps,
    secondaryOptions,
    secondaryKey
  };
}
